import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableSelectionModalComponent } from './table-selection-modal/table-selection-modal.component';
import { AuthenticationService } from './services/authentication.service';
import { LoaderService } from './services/loader.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { FileSizePipe } from './pipes/file-size.pipe';
import { SecondsDurationPipe } from './pipes/seconds-duration.pipe';
import { TimestampPipe } from './pipes/timestamp.pipe';
import { PageHeadingComponent } from './page-heading/page-heading.component';
import { MonitorService } from '../monitoring/shared/monitor.service';
import { ModalService } from './services/modal.service';
import { TranslateModule } from '@ngx-translate/core';
import { NavigationService } from './services/navigation.service';
import { NavigationLinkService } from './services/navigation-link.service';
import { AutofocusDirective } from './directives/autofocus.directive';
import { ClipboardService } from './services/clipboard.service';
import { PaginationComponent } from './pagination/pagination.component';
import { NoSanitizePipe } from './pipes/no-sanitize.pipe';
import { FullDatePipe } from './pipes/full-date.pipe';
import { AjaxPaginationComponent } from './ajax-pagination/ajax-pagination.component';
import { DropdownButtonComponent } from './dropdown-button/dropdown-button.component';
import { DropdownButtonItemComponent } from './dropdown-button/dropdown-button-item/dropdown-button-item.component';
import { DateDiffFromNowPipe } from './pipes/date-diff-from-now.pipe';
import { DataTableComponent } from './data-table/data-table.component';
import { DataTableColumnComponent } from './data-table/data-table-column/data-table-column.component';
import { DataFiltersComponent } from './data-filters/data-filters.component';
import { DataFilterInputComponent } from './data-filters/data-filter-input/data-filter-input.component';
import { DataFilterSelectComponent } from './data-filters/data-filter-select/data-filter-select.component';
import { DataFiltersService } from './data-filters/data-filters.service';
import { PaginationService } from './services/pagination.service';
import { DataFilterDateComponent } from './data-filters/data-filter-date/data-filter-date.component';
import { DpDatePickerModule } from 'ng2-date-picker';
import { DataButtonsComponent } from './data-buttons/data-buttons.component';
import { FileUploadModule } from 'ng2-file-upload';
import { FileDropComponent } from './file-drop/file-drop.component';
import { DataListComponent } from './data-list/data-list.component';
import { FileService } from './services/file.service';
import { DataFiltersButtonComponent } from './data-filters/data-filters-button/data-filters-button.component';
import { DataFilterRadioComponent } from './data-filters/data-filter-radio/data-filter-radio.component';
import { DataFilterCheckboxComponent } from './data-filters/data-filter-checkbox/data-filter-checkbox.component';
import { DataFilterGroupComponent } from './data-filters/data-filter-group/data-filter-group.component';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  DataFilterAutocompleteComponent
} from './data-filters/data-filter-autocomplete/data-filter-autocomplete.component';
import { DataFilterSwitchComponent } from './data-filters/data-filter-switch/data-filter-switch.component';
import { SwitcherComponent } from './switcher/switcher.component';
import { SwitcherFormComponent } from './switcher-form/switcher-form.component';
import { DragulaModule } from 'ng2-dragula';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { MapOverlayComponent } from './map-overlay/map-overlay.component';
import { MapOverlayContentComponent } from './map-overlay/map-overlay-content/map-overlay-content.component';
import { MapOverlayHeaderComponent } from './map-overlay/map-overlay-header/map-overlay-header.component';
import { AgmMarkerClustererModule } from '@agm/markerclusterer';
import { AgmMarkerSpiderModule } from 'agm-oms';
import { AgmSnazzyInfoWindowModule } from '@agm/snazzy-info-window';
import { PowerBiReportComponent } from './power-bi-report/power-bi-report.component';
import { PowerBiReportService } from './services/power-bi-report.service';
import { ErrorHandlerService } from './services/error-handler.service';
import { AddressPickerComponent } from './address-picker/address-picker.component';
import { AgmCoreModule } from '@agm/core';
import { AGM_CONFIG } from '../core/agm.config';
import { EtaApproximationFormComponent } from './eta-approximation-form/eta-approximation-form.component';
import {
  EtaApproximationItemComponent
} from './eta-approximation-form/eta-approximation-item/eta-approximation-item.component';
import { TabComponent } from './tabs-manager/tab/tab.component';
import { TabsComponent } from './tabs-manager/tabs/tabs.component';
import { JoinArrayPipe } from './pipes/join-array.pipe';
import { AutoCompleteAddressService } from './services/auto-complete-address.service';
import { RoutePathService } from './services/route-path.service';
import { GeocodingService } from './services/geocoding.service';
import { I18nTranslateDirective } from './services/i18n-translate.directive';
import { SapFlagsListComponent } from './sap-flags-list/sap-flags-list.component';
import { SapFlagsItemComponent } from './sap-flags-item/sap-flags-item.component';
import { SpacerComponent } from './spacer/spacer.component';
import { DropdownActionsComponent } from './dropdown-actions/dropdown-actions.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { DndDirective } from './directives/dnd.directive';
import {
  DataFilterMultiStringInputComponent
} from './data-filters/data-filter-multi-string-input/data-filter-multi-string-input.component';
import { AutoPageHeadingComponent } from './auto-page-heading/auto-page-heading.component';
import { DataFilterFieldsetComponent } from './data-filters/data-filter-fieldset/data-filter-fieldset.component';
import { ClipboardCopyButtonComponent } from './clipboard-copy-button/clipboard-copy-button.component';
import { TwoPressButtonComponent } from './two-press-button/two-press-button.component';
import { QuillModule } from 'ngx-quill';
import { CollapsibleComponent } from './collapsible/collapsible.component';
import { CollapsibleArrowComponent } from './collapsible/collapsible-arrow/collapsible-arrow.component';
import { FilePreviewComponent } from './file-preview/file-preview.component';
import { CsvColumnPreviewComponent } from './csv-column-preview/csv-column-preview.component';
import { InfoComponent } from './info/info.component';
import { ErrorComponent } from './error/error.component';
import { DeliveryTimelineComponent } from './delivery-timeline/delivery-timeline.component';
import { DataFilterMessageComponent } from './data-filters/data-filter-message/data-filter-message.component';
import { LoaderLogoComponent } from './loader-logo/loader-logo.component';
import { DeliveryEtaTypeIconComponent } from './delivery-eta-type-icon/delivery-eta-type-icon.component';
import { OnlyTimePipe } from './pipes/only-time.pipe';
import { BlueyonderDistancePipe } from './pipes/blueyonder-distance.pipe';
import { WidgetsService } from '../core/widgets/shared/widgets.service';
import { EmailValidatorService } from './services/email-validator.service';
import {
  DataFilterFormAutocompleteComponent
} from './data-filters/data-filter-autocomplete/data-filter-form-autocomplete.component';
import { CalloutComponent } from './callout/callout.component';
import { ResourceSecureBlobPipe } from './pipes/resource-secure-blob.pipe';
import { ResourceSecureUrlPipe } from './pipes/resource-secure-url.pipe';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { EtaDetailsPopupComponent } from './eta-details-popup/eta-details-popup.component';
import {
  NotificationAlertComponent
} from 'app/df-management/notification-alerts/notification-alert/notification-alert.component';
import { DfFullDatePipe } from './pipes/df-full-date.pipe';
import { OverlayPanelComponent } from './overlay-panel/overlay-panel.component';
import { FormatDatePipe } from './pipes/format-date.pipe';
import { FormatDatePipePipe } from './pipes/translate-duration.pipe';
import {
  DeliveryAndSalesOrderNumbersComponent
} from './delivery-and-sales-order-numbers/delivery-and-sales-order-numbers.component';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { DateIntervalPipe } from './pipes/date-interval.pipe';
import { FloatingFooterComponent } from './floating-footer/floating-footer.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DpDatePickerModule,
    FileUploadModule,
    NgSelectModule,
    DragulaModule.forRoot(),
    InfiniteScrollModule,
    AgmMarkerClustererModule,
    AgmMarkerSpiderModule,
    AgmSnazzyInfoWindowModule,
    AgmCoreModule.forRoot(AGM_CONFIG),
    ReactiveFormsModule,
    QuillModule.forRoot(),
    NgxIntlTelInputModule,
    PopoverModule
  ],
  declarations: [
    AjaxPaginationComponent,
    DataTableComponent,
    DataButtonsComponent,
    DataTableColumnComponent,
    DataFiltersComponent,
    DataFiltersButtonComponent,
    DataFilterDateComponent,
    DataFilterGroupComponent,
    DataFilterInputComponent,
    DataFilterMultiStringInputComponent,
    DataFilterRadioComponent,
    DataFilterSelectComponent,
    DataFilterAutocompleteComponent,
    DataFilterFormAutocompleteComponent,
    DataFilterSwitchComponent,
    DataFilterCheckboxComponent,
    DataFilterMessageComponent,
    DataListComponent,
    DropdownButtonComponent,
    DropdownButtonItemComponent,
    FileDropComponent,
    MapOverlayComponent,
    MapOverlayContentComponent,
    MapOverlayHeaderComponent,
    PaginationComponent,
    PageHeadingComponent,
    SwitcherComponent,
    SwitcherFormComponent,
    TableSelectionModalComponent,
    PowerBiReportComponent,
    EtaApproximationFormComponent,
    EtaApproximationItemComponent,
    TabComponent,
    TabsComponent,
    DateDiffFromNowPipe,
    DateIntervalPipe,
    FileSizePipe,
    FullDatePipe,
    FormatDatePipe,
    NoSanitizePipe,
    SecondsDurationPipe,
    JoinArrayPipe,
    TimestampPipe,
    AutofocusDirective,
    AddressPickerComponent,
    I18nTranslateDirective,
    SapFlagsListComponent,
    SapFlagsItemComponent,
    SpacerComponent,
    FileUploadComponent,
    DndDirective,
    DropdownActionsComponent,
    AutoPageHeadingComponent,
    DataFilterFieldsetComponent,
    ClipboardCopyButtonComponent,
    TwoPressButtonComponent,
    CollapsibleComponent,
    CollapsibleArrowComponent,
    FilePreviewComponent,
    CsvColumnPreviewComponent,
    InfoComponent,
    ErrorComponent,
    LoaderLogoComponent,
    DeliveryEtaTypeIconComponent,
    DeliveryTimelineComponent,
    OnlyTimePipe,
    BlueyonderDistancePipe,
    CalloutComponent,
    ResourceSecureBlobPipe,
    ResourceSecureUrlPipe,
    EtaDetailsPopupComponent,
    NotificationAlertComponent,
    OverlayPanelComponent,
    DfFullDatePipe,
    FormatDatePipePipe,
    DeliveryAndSalesOrderNumbersComponent,
    FloatingFooterComponent
  ],
  exports: [
    TwoPressButtonComponent,
    CommonModule,
    FormsModule,
    HttpClientModule,
    TranslateModule,
    FileUploadModule,
    NgSelectModule,
    AgmMarkerClustererModule,
    AgmMarkerSpiderModule,
    AgmSnazzyInfoWindowModule,
    AgmCoreModule,
    DpDatePickerModule,
    AjaxPaginationComponent,
    DataTableComponent,
    DataButtonsComponent,
    DataTableColumnComponent,
    DataFiltersComponent,
    DataFiltersButtonComponent,
    DataFilterDateComponent,
    DataFilterGroupComponent,
    DataFilterInputComponent,
    DataFilterMultiStringInputComponent,
    DataFilterRadioComponent,
    DataFilterCheckboxComponent,
    DataFilterSelectComponent,
    DataFilterAutocompleteComponent,
    DataFilterFormAutocompleteComponent,
    DataFilterSwitchComponent,
    DataFilterMessageComponent,
    DataListComponent,
    DropdownButtonComponent,
    DropdownButtonItemComponent,
    FileDropComponent,
    MapOverlayComponent,
    MapOverlayContentComponent,
    MapOverlayHeaderComponent,
    PageHeadingComponent,
    PaginationComponent,
    SwitcherComponent,
    SwitcherFormComponent,
    TableSelectionModalComponent,
    PowerBiReportComponent,
    AddressPickerComponent,
    EtaApproximationFormComponent,
    EtaApproximationItemComponent,
    TabComponent,
    TabsComponent,
    DateDiffFromNowPipe,
    FileSizePipe,
    FullDatePipe,
    NoSanitizePipe,
    SecondsDurationPipe,
    TimestampPipe,
    AutofocusDirective,
    JoinArrayPipe,
    I18nTranslateDirective,
    SapFlagsListComponent,
    SapFlagsItemComponent,
    SpacerComponent,
    FileUploadComponent,
    DndDirective,
    DropdownActionsComponent,
    ClipboardCopyButtonComponent,
    AutoPageHeadingComponent,
    DataFilterFieldsetComponent,
    CollapsibleComponent,
    CollapsibleArrowComponent,
    FilePreviewComponent,
    CsvColumnPreviewComponent,
    InfoComponent,
    ErrorComponent,
    LoaderLogoComponent,
    DeliveryEtaTypeIconComponent,
    DeliveryTimelineComponent,
    OnlyTimePipe,
    BlueyonderDistancePipe,
    CalloutComponent,
    ResourceSecureBlobPipe,
    ResourceSecureUrlPipe,
    NgxIntlTelInputModule,
    EtaDetailsPopupComponent,
    NotificationAlertComponent,
    OverlayPanelComponent,
    DfFullDatePipe,
    FormatDatePipe,
    FormatDatePipePipe,
    DeliveryAndSalesOrderNumbersComponent,
    PopoverModule,
    DateIntervalPipe,
    FloatingFooterComponent
  ],
  providers: [
    AutoCompleteAddressService,
    RoutePathService,
    GeocodingService,
    AuthenticationService,
    ClipboardService,
    DataFiltersService,
    EmailValidatorService,
    ErrorHandlerService,
    LoaderService,
    MonitorService,
    ModalService,
    NavigationService,
    NavigationLinkService,
    PaginationService,
    FileService,
    PowerBiReportService,
    WidgetsService,
    DateDiffFromNowPipe,
    FullDatePipe,
    SecondsDurationPipe,
    FileSizePipe,
    ResourceSecureBlobPipe,
    ResourceSecureUrlPipe,
    DfFullDatePipe
  ]
})
export class SharedModule {
}
